import { useState } from "react";
import { Navigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { user, signIn } = useAuth();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/";

  // Test toast removed - now testing actual error flow

  // Redirect if already logged in
  if (user) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log("Form submitted, prevented default");

    if (!email || !password) {
      console.log("Empty fields, showing toast");
      toast.error("Please fill in all fields");
      return;
    }

    setIsLoading(true);

    try {
      await signIn(email, password);
      // Don't show success toast - user will be redirected immediately
    } catch (error: any) {
      console.error("Login error caught in handleSubmit:", error);
      // Ensure we show the error toast and prevent any redirect
      setIsLoading(false);
      console.log("About to show error toast...");
      toast.error(
        error.message || "Invalid email or password. Please try again.",
      );
      console.log("Error toast called");
      return; // Explicit return to prevent any further execution
    }

    setIsLoading(false);
  };

  return (
    <div className="flex h-dvh w-screen items-start pt-12 md:pt-0 md:items-center justify-center bg-background">
      <div className="w-full max-w-md overflow-hidden rounded-2xl flex flex-col gap-12">
        <div className="flex flex-col items-center justify-center gap-2 px-4 text-center sm:px-16">
          <h3 className="text-xl font-semibold dark:text-zinc-50">Sign In</h3>
          <p className="text-sm text-gray-500 dark:text-zinc-400">
            Use your email and password to sign in
          </p>
        </div>

        <form
          className="flex flex-col gap-4 px-4 sm:px-16"
          onSubmit={handleSubmit}
        >
          <div className="flex flex-col gap-2">
            <label
              htmlFor="email"
              className="text-zinc-600 font-normal dark:text-zinc-400"
            >
              Email Address
            </label>
            <input
              id="email"
              name="email"
              className="text-md md:text-sm mt-1 block w-full px-3 py-2 border border-input bg-background rounded-md shadow-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              type="email"
              placeholder="user@acme.com"
              autoComplete="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label
              htmlFor="password"
              className="text-zinc-600 font-normal dark:text-zinc-400"
            >
              Password
            </label>
            <input
              id="password"
              name="password"
              className="text-md md:text-sm mt-1 block w-full px-3 py-2 border border-input bg-background rounded-md shadow-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full size-4 border-b-2 border-primary-foreground mr-2"></div>
                Signing in...
              </div>
            ) : (
              "Sign in"
            )}
          </button>

          <div className="mt-4 space-y-2">
            <p className="text-center text-sm text-gray-600 dark:text-zinc-400">
              <Link
                to="/forgot-password"
                className="font-semibold text-gray-800 hover:underline dark:text-zinc-200"
              >
                Forgot your password?
              </Link>
            </p>
            <p className="text-center text-sm text-gray-600 dark:text-zinc-400">
              {"Don't have an account? "}
              <Link
                to="/register"
                className="font-semibold text-gray-800 hover:underline dark:text-zinc-200"
              >
                Sign up
              </Link>
              {" for free."}
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
